<template>
  <AppLayout>
    <div class="page">
      <SemStepper active-key="step3" />

      <div class="content">
        <aside class="left">
          <div class="left-head">
            <div class="left-title">SEM图片导入</div>
            <button class="ghost" type="button" @click="goPrev">返回上一步</button>
          </div>
          <div
            v-if="taskMeta && (taskMeta.materialStructure || taskMeta.materialComposition)"
            class="meta-block"
          >
            <div class="meta-row"><span class="mk">材料结构</span><span class="mv">{{ taskMeta.materialStructure || '—' }}</span></div>
            <div class="meta-row"><span class="mk">材料组成</span><span class="mv">{{ taskMeta.materialComposition || '—' }}</span></div>
            <div v-if="taskMeta.importMode === 'order' && (taskMeta.orderLabel || taskMeta.orderId)" class="meta-row">
              <span class="mk">订单</span><span class="mv">{{ taskMeta.orderLabel || taskMeta.orderId }}</span>
            </div>
          </div>
        </aside>

        <section class="center">
          <div class="topbar">
            <div class="topbar-title">结果预览</div>
          </div>
          <div class="canvas">
            <div class="img-stage">
              <img v-if="currentSrc" class="canvas-img" :src="currentSrc" alt="SEM result" />
              <svg
                v-if="currentSrc && measurementLines.length && measurementNaturalW > 0 && measurementNaturalH > 0"
                class="measure-overlay"
                :viewBox="`0 0 ${measurementNaturalW} ${measurementNaturalH}`"
                preserveAspectRatio="xMidYMid meet"
              >
                <g v-for="m in measurementLines" :key="m.id">
                  <line :x1="m.start.x" :y1="m.start.y" :x2="m.end.x" :y2="m.end.y" class="measure-line" />
                  <text
                    :x="(m.start.x + m.end.x) / 2 + 6"
                    :y="(m.start.y + m.end.y) / 2 - 6"
                    class="measure-text"
                  >
                    {{ formatMeasureLabel(m.start, m.end) }}
                  </text>
                </g>
              </svg>
              <div v-else class="canvas-ph">暂无结果图</div>
            </div>
          </div>
          <div class="footer">
            <button class="secondary" type="button" @click="goPrev">上一步</button>
            <button class="secondary" type="button" @click="exportDataZip">导出数据</button>
          </div>
        </section>

        <aside class="right">
          <div class="panel">
            <div class="panel-title">粒径信息与分布</div>
            <template v-if="taskMeta && (taskMeta.materialStructure || taskMeta.materialComposition)">
              <div class="kv"><div class="k">材料结构</div><div class="v">{{ taskMeta.materialStructure || '—' }}</div></div>
              <div class="kv"><div class="k">材料组成</div><div class="v">{{ taskMeta.materialComposition || '—' }}</div></div>
              <div v-if="taskMeta.importMode === 'order' && (taskMeta.orderLabel || taskMeta.orderId)" class="kv">
                <div class="k">订单号</div><div class="v">{{ taskMeta.orderLabel || taskMeta.orderId }}</div>
              </div>
            </template>
            <div class="kv"><div class="k">颗粒数量</div><div class="v">{{ summary.count ?? '-' }}</div></div>
            <div class="kv"><div class="k">平均等效粒径</div><div class="v">{{ formatNumber3(summary.mean) }} {{ summary.unit || scaleUnit }}</div></div>
            <div class="kv"><div class="k">标准差</div><div class="v">{{ formatNumber3(summary.std) }} {{ summary.unit || scaleUnit }}</div></div>
            <div class="kv"><div class="k">P10</div><div class="v">{{ formatNumber3(summary.p10) }} {{ summary.unit || scaleUnit }}</div></div>
            <div class="kv"><div class="k">P50</div><div class="v">{{ formatNumber3(summary.p50) }} {{ summary.unit || scaleUnit }}</div></div>
            <div class="kv"><div class="k">P90</div><div class="v">{{ formatNumber3(summary.p90) }} {{ summary.unit || scaleUnit }}</div></div>
            <div class="chart">
              <div class="chart-title">粒径分布图</div>
              <div class="dist-wrap"><canvas ref="distCanvas" class="dist-canvas"></canvas></div>
              <div class="chart-x"><span>{{ formatNumber3(histogramMin) }}</span><span>{{ formatNumber3(histogramMax) }}</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '../../../layouts/AppLayout.vue';
import SemStepper from '../../../components/SemStepper.vue';
import JSZip from 'jszip';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType,
  ImageRun,
} from 'docx';
import { getSemTask, getSemTaskSteps } from '../../../services/semApi';

const SEM_TASK_STORAGE_KEY = 'sem_current_task_id';

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default {
  name: 'SemStep3Export',
  components: { AppLayout, SemStepper },
  data() {
    return {
      taskId: this.$route.query.taskId || null,
      previewUrl: null,
      overlayUrl: null,
      labelUrl: null,
      scaleValue: 100,
      scalePxLength: null,
      scaleUnit: 'μm',
      stats: null,
      editedOverlayUrl: null,
      taskMeta: null,
      measurementLines: [],
      measurementNaturalW: 0,
      measurementNaturalH: 0,
    };
  },
  computed: {
    currentSrc() {
      return this.editedOverlayUrl || this.overlayUrl || this.previewUrl || null;
    },
    summary() {
      return this.stats?.summary || {};
    },
    histogram() {
      return this.stats?.histogram || [];
    },
    histogramMin() {
      if (!this.histogram.length) return '-';
      return this.histogram[0].from;
    },
    histogramMax() {
      if (!this.histogram.length) return '-';
      return this.histogram[this.histogram.length - 1].to;
    },
  },
  mounted() {
    this.resolveTaskId();
    if (!this.taskId) {
      this.$router.push({ name: 'semLanding' });
      return;
    }
    this.loadFromDb();
  },
  methods: {
    resolveTaskId() {
      if (this.taskId) {
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
        return;
      }
      const cached = sessionStorage.getItem(SEM_TASK_STORAGE_KEY);
      if (cached) {
        this.taskId = cached;
        this.$router.replace({ query: { ...this.$route.query, taskId: cached } });
      }
    },
    async loadFromDb() {
      sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
      try {
        const isLikelyLabelMap = (url) => /(^|\/)label_[^/]*\.png(\?|$)/i.test(String(url || ''));
        const parseMaybeJson = (v) => {
          if (!v) return null;
          if (typeof v === 'object') return v;
          if (typeof v === 'string') {
            try { return JSON.parse(v); } catch (e) { return null; }
          }
          return null;
        };
        const [task, steps] = await Promise.all([
          getSemTask(this.taskId),
          getSemTaskSteps(this.taskId),
        ]);
        this.previewUrl = task?.img_url || null;
        if (task?.scale_value !== null && task?.scale_value !== '' && Number.isFinite(Number(task?.scale_value))) {
          this.scaleValue = Number(task.scale_value);
        }
        if (task?.scale_px_length !== null && task?.scale_px_length !== '' && Number.isFinite(Number(task?.scale_px_length))) {
          this.scalePxLength = Number(task.scale_px_length);
        }
        if (task?.scale_unit) this.scaleUnit = task.scale_unit;
        const taskRuler = parseMaybeJson(task?.ruler_info);
        if (
          (!Number.isFinite(Number(this.scaleValue)) || this.scaleValue <= 0)
          && Number.isFinite(Number(taskRuler?.scale?.value || taskRuler?.value))
        ) {
          this.scaleValue = Number(taskRuler?.scale?.value || taskRuler?.value);
        }
        if (
          (!Number.isFinite(Number(this.scalePxLength)) || this.scalePxLength <= 0)
          && Number.isFinite(Number(taskRuler?.bar?.pxLength || taskRuler?.pxLength))
        ) {
          this.scalePxLength = Number(taskRuler?.bar?.pxLength || taskRuler?.pxLength);
        }
        this.taskMeta = {
          materialStructure: task?.material_structure || '',
          materialComposition: task?.material_composition || '',
          importMode: 'local',
          scaleValue: this.scaleValue,
          scalePxLength: this.scalePxLength,
        };
        const segSteps = (steps || []).filter((s) => s?.step_type === 'segment' && s?.status === 'success');
        const baseSeg = segSteps.find((s) => s.api_path === '/api/sem/segment') || segSteps[0] || null;
        const editedSeg = segSteps.find((s) => s.api_path === '/api/sem/task/update') || null;
        if (baseSeg) {
          this.overlayUrl = baseSeg.overlay_url || null;
          this.labelUrl = baseSeg.label_url || null;
          const resp = parseMaybeJson(baseSeg?.response_data);
          if (resp?.stats) this.stats = resp.stats;
        }
        if (editedSeg) {
          const editedFromStep = editedSeg.label_url || null;
          this.editedOverlayUrl = isLikelyLabelMap(editedFromStep) ? null : editedFromStep;
          const resp2 = parseMaybeJson(editedSeg?.response_data);
          if (resp2?.stats) this.stats = resp2.stats;
          const ms = resp2?.measurements || null;
          this.measurementLines = Array.isArray(ms?.lines) ? ms.lines : [];
          this.measurementNaturalW = Number(ms?.naturalW) || 0;
          this.measurementNaturalH = Number(ms?.naturalH) || 0;
        }
        const latestStats = parseMaybeJson(task?.latest_stats);
        if (latestStats && latestStats.masks) this.stats = latestStats;
        if (task?.edited_overlay_url && !isLikelyLabelMap(task.edited_overlay_url)) {
          this.editedOverlayUrl = task.edited_overlay_url;
        }
        const taskMs = parseMaybeJson(task?.measurements);
        if (taskMs && Array.isArray(taskMs.lines)) {
          this.measurementLines = taskMs.lines;
          this.measurementNaturalW = Number(taskMs.naturalW) || this.measurementNaturalW;
          this.measurementNaturalH = Number(taskMs.naturalH) || this.measurementNaturalH;
        }
      } catch (e) {
        console.error(e);
      }
      this.$nextTick(() => this.drawDistribution());
    },
    formatNumber3(v) {
      if (v == null || !Number.isFinite(Number(v))) return '-';
      return Number(v).toFixed(3);
    },
    formatMeasureLabel(start, end) {
      const sx = Number(start?.x);
      const sy = Number(start?.y);
      const ex = Number(end?.x);
      const ey = Number(end?.y);
      if (![sx, sy, ex, ey].every((n) => Number.isFinite(n))) return '-';
      const pxLen = Math.hypot(ex - sx, ey - sy);
      const scalePx = Number(this.scalePxLength);
      const scaleVal = Number(this.scaleValue);
      if (Number.isFinite(scalePx) && Number.isFinite(scaleVal) && scalePx > 0) {
        const v = (pxLen * scaleVal) / scalePx;
        return `${v.toFixed(3)} ${this.scaleUnit || 'μm'}`;
      }
      return `${pxLen.toFixed(3)} ${this.scaleUnit || 'μm'}`;
    },
    goPrev() {
      sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
      const to = this.$router.resolve({
        name: 'semStep2',
        query: { taskId: this.taskId },
      });
      window.location.assign(to.href);
    },
    drawDistribution() {
      const canvas = this.$refs.distCanvas;
      if (!canvas) return;
      const hist = this.histogram;
      const cssW = canvas.clientWidth || 1;
      const cssH = canvas.clientHeight || 1;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);
      if (!hist.length) return;
      const marginL = 46;
      const marginR = 12;
      const marginT = 12;
      const marginB = 42;
      const usableW = cssW - marginL - marginR;
      const usableH = cssH - marginT - marginB;
      const baselineY = marginT + usableH;
      const minX = hist[0].from;
      const maxX = hist[hist.length - 1].to;
      const maxV = Math.max(...hist.map((b) => Number(b.value) || 0), 1);
      const rangeX = Math.max(1e-9, maxX - minX);
      const xMap = (x) => marginL + ((x - minX) / rangeX) * usableW;
      const yMap = (v) => baselineY - (v / maxV) * usableH;

      // 坐标轴与刻度
      ctx.save();
      ctx.strokeStyle = 'rgba(100, 116, 139, 0.75)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(marginL, marginT);
      ctx.lineTo(marginL, baselineY);
      ctx.lineTo(cssW - marginR, baselineY);
      ctx.stroke();

      ctx.fillStyle = 'rgba(71, 85, 105, 0.95)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let i = 0; i <= 4; i += 1) {
        const y = baselineY - (usableH * i) / 4;
        const v = (maxV * i) / 4;
        ctx.beginPath();
        ctx.moveTo(marginL - 4, y);
        ctx.lineTo(marginL, y);
        ctx.stroke();
        ctx.fillText(String(Math.round(v)), marginL - 6, y);
      }

      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let i = 0; i <= 4; i += 1) {
        const x = marginL + (usableW * i) / 4;
        const v = minX + (rangeX * i) / 4;
        ctx.beginPath();
        ctx.moveTo(x, baselineY);
        ctx.lineTo(x, baselineY + 4);
        ctx.stroke();
        ctx.fillText(this.formatNumber3(v), x, baselineY + 6);
      }

      const unit = this.summary?.unit || this.scaleUnit || 'μm';
      ctx.fillText(`粒径 (${unit})`, marginL + usableW / 2, cssH - 14);
      ctx.save();
      ctx.translate(13, marginT + usableH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('数量', 0, 0);
      ctx.restore();
      ctx.restore();

      ctx.save();
      ctx.fillStyle = 'rgba(59, 130, 246, 0.18)';
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.65)';
      ctx.lineWidth = 1.5;
      const first = hist[0];
      ctx.beginPath();
      ctx.moveTo(xMap(first.from), baselineY);
      for (const b of hist) {
        const xL = xMap(b.from);
        const xR = xMap(b.to);
        const y = yMap(Number(b.value) || 0);
        ctx.lineTo(xL, y);
        ctx.lineTo(xR, y);
      }
      const last = hist[hist.length - 1];
      ctx.lineTo(xMap(last.to), baselineY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    },
    async fetchBlobFromUrl(url) {
      if (!url) return null;
      if (url.startsWith('data:')) {
        const r = await fetch(url);
        return r.blob();
      }
      const r = await fetch(url, { mode: 'cors' });
      if (!r.ok) return null;
      return r.blob();
    },
    async buildEditedOverlayBlobWithMeasurements() {
      const src = this.editedOverlayUrl || this.currentSrc || '';
      const baseBlob = await this.fetchBlobFromUrl(src);
      if (!baseBlob) return null;
      if (!this.measurementLines.length || !this.measurementNaturalW || !this.measurementNaturalH) return baseBlob;

      const url = URL.createObjectURL(baseBlob);
      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
        const w = Number(img.naturalWidth || this.measurementNaturalW || 0);
        const h = Number(img.naturalHeight || this.measurementNaturalH || 0);
        if (!w || !h) return baseBlob;

        const sx = w / this.measurementNaturalW;
        const sy = h / this.measurementNaturalH;

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return baseBlob;
        ctx.drawImage(img, 0, 0, w, h);

        ctx.save();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#ecfeff';
        for (const m of this.measurementLines) {
          const x1 = Number(m?.start?.x) * sx;
          const y1 = Number(m?.start?.y) * sy;
          const x2 = Number(m?.end?.x) * sx;
          const y2 = Number(m?.end?.y) * sy;
          if (![x1, y1, x2, y2].every((n) => Number.isFinite(n))) continue;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          const label = this.formatMeasureLabel(m.start, m.end);
          const tx = (x1 + x2) / 2 + 6;
          const ty = (y1 + y2) / 2 - 6;
          ctx.strokeStyle = 'rgba(15,23,42,0.85)';
          ctx.lineWidth = 3;
          ctx.strokeText(label, tx, ty);
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;
          ctx.fillText(label, tx, ty);
        }
        ctx.restore();

        const outBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        return outBlob || baseBlob;
      } catch (e) {
        console.error('buildEditedOverlayBlobWithMeasurements failed:', e);
        return baseBlob;
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    async exportDataZip() {
      const zip = new JSZip();
      const baseName = `SEM_${this.taskId || Date.now()}`;
      const hist = this.histogram || [];
      const masks = this.stats?.masks || [];
      const s = this.summary || {};
      const now = new Date();
      const unit = s.unit || this.scaleUnit || 'μm';

      const chartCanvas = this.$refs.distCanvas;
      let chartBlob = null;
      if (chartCanvas) {
        chartBlob = await new Promise((resolve) => chartCanvas.toBlob(resolve, 'image/png'));
      }

      const editedWithMeasureBlob = await this.buildEditedOverlayBlobWithMeasurements();
      const imageSources = [
        { title: '原始图像', blob: await this.fetchBlobFromUrl(this.previewUrl || '') },
        { title: '分割后图像', blob: await this.fetchBlobFromUrl(this.overlayUrl || '') },
        { title: '修改分割后图像', blob: editedWithMeasureBlob },
        { title: '粒径分布图', blob: chartBlob },
      ];

      const docxBlob = await this.generateReportDocx({
        taskId: this.taskId || '-',
        time: now.toLocaleString(),
        summary: s,
        histogram: hist,
        masks,
        unit,
        imageSources,
        taskMeta: this.taskMeta,
      });
      zip.file(`${baseName}.docx`, docxBlob);

      const out = await zip.generateAsync({ type: 'blob' });
      downloadBlob(`${baseName}_导出数据.zip`, out);
    },
    async blobToBytes(blob) {
      if (!blob) return null;
      const buf = await blob.arrayBuffer();
      return new Uint8Array(buf);
    },
    async getImageNaturalSize(blob) {
      if (!blob) return null;
      const url = URL.createObjectURL(blob);
      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
        const w = Number(img.naturalWidth || 0);
        const h = Number(img.naturalHeight || 0);
        if (!w || !h) return null;
        return { w, h };
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    fitSizeKeepRatio(w, h, maxW, maxH) {
      if (!w || !h) return { width: maxW, height: maxH };
      const scale = Math.min(maxW / w, maxH / h, 1);
      return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale)),
      };
    },
    async generateReportDocx(data) {
      const sum = data.summary || {};
      const unit = data.unit || this.scaleUnit || 'μm';
      const hist = Array.isArray(data.histogram) ? data.histogram : [];
      const masks = Array.isArray(data.masks) ? data.masks : [];
      const tm = data.taskMeta || null;

      const children = [
        new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun('SEM 数据报告')] }),
        new Paragraph(`任务ID：${data.taskId}`),
        new Paragraph(`导出时间：${data.time}`),
      ];
      if (tm && (tm.materialStructure || tm.materialComposition)) {
        children.push(new Paragraph(`材料结构：${tm.materialStructure || '—'}`));
        children.push(new Paragraph(`材料组成：${tm.materialComposition || '—'}`));
      }
      children.push(
        new Paragraph(''),
        new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun('粒径信息与分布（表格）')] }),
        new Paragraph(''),
      );

      const summaryRows = [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('统计项')] }),
            new TableCell({ children: [new Paragraph('值')] }),
            new TableCell({ children: [new Paragraph('单位')] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('颗粒数量')] }),
            new TableCell({ children: [new Paragraph(String(sum.count ?? '-'))] }),
            new TableCell({ children: [new Paragraph('')] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('平均等效粒径')] }),
            new TableCell({ children: [new Paragraph(this.formatNumber3(sum.mean))] }),
            new TableCell({ children: [new Paragraph(unit)] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('标准差')] }),
            new TableCell({ children: [new Paragraph(this.formatNumber3(sum.std))] }),
            new TableCell({ children: [new Paragraph(unit)] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('P10')] }),
            new TableCell({ children: [new Paragraph(this.formatNumber3(sum.p10))] }),
            new TableCell({ children: [new Paragraph(unit)] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('P50')] }),
            new TableCell({ children: [new Paragraph(this.formatNumber3(sum.p50))] }),
            new TableCell({ children: [new Paragraph(unit)] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('P90')] }),
            new TableCell({ children: [new Paragraph(this.formatNumber3(sum.p90))] }),
            new TableCell({ children: [new Paragraph(unit)] }),
          ],
        }),
      ];
      children.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: summaryRows }));
      children.push(
        new Paragraph(''),
        new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun('作图数据（x轴粒径区间 / y轴-颗粒数）')] }),
      );

      const plotRows = [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('x轴粒径区间')] }),
            new TableCell({ children: [new Paragraph('y轴-颗粒数')] }),
          ],
        }),
      ];
      hist.forEach((b) => {
        plotRows.push(
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph(`${Number(b.from).toFixed(3)}~${Number(b.to).toFixed(3)}`)] }),
              new TableCell({ children: [new Paragraph(String(Number(b.value) || 0))] }),
            ],
          }),
        );
      });
      children.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: plotRows }));

      children.push(new Paragraph(''));
      children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun('详细原始数据')] }));

      const detailRows = [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('序号')] }),
            new TableCell({ children: [new Paragraph('粒径')] }),
          ],
        }),
      ];
      masks.forEach((m, i) => {
        const d = Number(m?.equivDiameter);
        detailRows.push(
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph(String(i + 1))] }),
              new TableCell({ children: [new Paragraph(Number.isFinite(d) ? d.toFixed(3) : '')] }),
            ],
          }),
        );
      });
      children.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: detailRows }));

      children.push(new Paragraph(''));
      children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun('分割筛选图（表格）')] }));

      const imgs = Array.isArray(data.imageSources) ? data.imageSources : [];
      const imageRows = [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph('图像类型')] }),
            new TableCell({ children: [new Paragraph('图像')] }),
          ],
        }),
      ];
      // eslint-disable-next-line no-restricted-syntax
      for (const it of imgs) {
        if (!it?.blob) continue;
        // eslint-disable-next-line no-await-in-loop
        const bytes = await this.blobToBytes(it.blob);
        // eslint-disable-next-line no-await-in-loop
        const size = await this.getImageNaturalSize(it.blob);
        if (!bytes || !size) continue;
        const fit = this.fitSizeKeepRatio(size.w, size.h, 460, 280);
        imageRows.push(
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph(it.title || '')] }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new ImageRun({
                        data: bytes,
                        transformation: { width: fit.width, height: fit.height },
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        );
      }
      children.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: imageRows }));

      const doc = new Document({
        sections: [{ children }],
      });
      return Packer.toBlob(doc);
    },
    exportCsv() {
      const s = this.summary || {};
      const lines = [
        'metric,value,unit',
        `count,${s.count ?? ''},`,
        `mean,${s.mean ?? ''},${s.unit || this.scaleUnit}`,
        `std,${s.std ?? ''},${s.unit || this.scaleUnit}`,
        `p10,${s.p10 ?? ''},${s.unit || this.scaleUnit}`,
        `p50,${s.p50 ?? ''},${s.unit || this.scaleUnit}`,
        `p90,${s.p90 ?? ''},${s.unit || this.scaleUnit}`,
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      downloadBlob(`sem_stats_${this.taskId || 'task'}.csv`, blob);
    },
  },
};
</script>

<style scoped>
.page { display: flex; flex-direction: column; gap: 10px; flex: 1; min-height: 0; }
.content { display: grid; grid-template-columns: 180px 1fr 440px; gap: 10px; align-items: stretch; flex: 1; min-height: 0; }
.left { background: rgba(255, 255, 255, 0.75); border: 1px solid rgba(148, 163, 184, 0.28); border-radius: 14px; padding: 10px; overflow: auto; }
.left-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.left-title { font-size: 12px; font-weight: 700; color: #0f172a; }
.ghost { border: 1px solid rgba(148, 163, 184, 0.4); background: rgba(255, 255, 255, 0.8); border-radius: 10px; padding: 6px 10px; font-size: 11px; cursor: pointer; }
.meta-block {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.22);
  font-size: 11px;
  line-height: 1.45;
}
.meta-row {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 6px;
  padding: 3px 0;
}
.meta-row + .meta-row { border-top: 1px dashed rgba(148, 163, 184, 0.28); }
.mk { color: #64748b; }
.mv { color: #0f172a; word-break: break-all; }
.center { display: flex; flex-direction: column; gap: 10px; min-height: 0; height: 100%; }
.topbar { background: rgba(255, 255, 255, 0.78); border: 1px solid rgba(148, 163, 184, 0.28); border-radius: 14px; padding: 10px 12px; }
.topbar-title { font-size: 14px; font-weight: 800; color: #0f172a; }
.canvas { background: rgba(255, 255, 255, 0.75); border: 1px solid rgba(148, 163, 184, 0.28); border-radius: 14px; overflow: hidden; width: 100%; flex: 1 1 auto; min-height: 0; display: flex; align-items: center; justify-content: center; box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12); }
.img-stage { position: relative; width: 100%; height: 100%; background: #0b1220; display: flex; align-items: center; justify-content: center; }
.canvas-img { width: 100%; height: 100%; object-fit: contain; background: #0b1220; }
.canvas-ph { color: #64748b; font-size: 12px; padding: 16px; }
.measure-overlay { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.measure-line { stroke: #3b82f6; stroke-width: 2; }
.measure-text { font-size: 12px; fill: #ecfeff; paint-order: stroke; stroke: rgba(15, 23, 42, 0.8); stroke-width: 3px; stroke-linejoin: round; }
.footer { display: flex; gap: 10px; margin-top: auto; padding-top: 4px; }
.secondary { border: 1px solid rgba(148, 163, 184, 0.45); background: #ffffff; border-radius: 10px; padding: 8px 16px; font-size: 12px; cursor: pointer; }
.right .panel { background: rgba(255, 255, 255, 0.75); border: 1px solid rgba(148, 163, 184, 0.28); border-radius: 14px; padding: 12px; height: 100%; min-height: 0; overflow: auto; display: flex; flex-direction: column; }
.panel-title { font-size: 13px; font-weight: 900; margin-bottom: 10px; }
.kv { display: grid; grid-template-columns: 90px 1fr; gap: 8px; font-size: 11px; padding: 6px 0; border-top: 1px dashed rgba(148, 163, 184, 0.28); }
.k { color: #64748b; }
.v { color: #0f172a; word-break: break-all; }
.chart { margin-top: 10px; flex: 1 1 auto; min-height: 0; overflow: hidden; }
.chart-title { font-size: 12px; font-weight: 800; margin-bottom: 8px; color: #0f172a; }
.dist-wrap { width: 100%; height: 220px; border-radius: 12px; border: 1px solid rgba(148, 163, 184, 0.22); background: rgba(255, 255, 255, 0.45); overflow: hidden; }
.dist-canvas { width: 100%; height: 100%; display: block; }
.chart-x { display: flex; justify-content: space-between; font-size: 10px; color: #64748b; margin-top: 6px; }
@media (max-width: 1100px) { .content { grid-template-columns: 1fr; } }
</style>

